import type { ReactNode } from 'react';
import { Box } from '@mui/material';

/**
 * The colour triplet a {@link Badge} needs. Shared contract so every status→colour
 * map across features (badge variants, feature status badges) speaks the same shape.
 */
export type BadgeColors = {
  color: string;
  bgcolor: string;
  dot: string;
};

export interface BadgeProps {
  label?: string;
  children?: ReactNode;
  color?: string;
  bgcolor?: string;
  border?: boolean;
  borderColor?: string;
  dot?: string;
  dotGlow?: boolean;
  fontWeight?: number;
  letterSpacing?: string;
}

export function Badge({
  label,
  children,
  color,
  bgcolor,
  border,
  borderColor,
  dot,
  dotGlow,
  fontWeight,
  letterSpacing,
}: BadgeProps) {
  return (
    <Box
      component="span"
      sx={theme => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: dot ? 0.75 : undefined,
        height: theme.chip.sm,
        px: 1.25,
        borderRadius: theme.borderRadius.pill,
        bgcolor: bgcolor ?? 'background.paper',
        color: color ?? theme.palette.text.body,
        fontSize: theme.typography.caption.fontSize,
        fontWeight: fontWeight ?? theme.typography.fontWeightRegular,
        letterSpacing: letterSpacing ?? 'normal',
        ...(border && { border: '1px solid', borderColor: borderColor ?? 'divider' }),
      })}
    >
      {dot && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: dot,
            flexShrink: 0,
            ...(dotGlow && { boxShadow: `0 0 0 3px ${dot}40` }),
          }}
        />
      )}
      {children ?? label}
    </Box>
  );
}
